import React, { useCallback, useEffect, useState } from "react";
import { FileText, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { BotonUpim, EncabezadoUpim, SeccionUpim, TarjetaUpim } from "../components/ui-upim";
import { fiscalAPI, getToken } from "../services/api";

const API = process.env.REACT_APP_BACKEND_URL;
const VACIO = {
  person_type: "fisica", rfc: "", legal_name: "", zip_code: "",
  tax_regime: "", cfdi_use: "", invoice_email: "", consent_privacy: false,
};

const cabeceras = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken() || ""}`,
});

export default function PerfilFiscalPage() {
  const [form, setForm] = useState(VACIO);
  const [catalogos, setCatalogos] = useState({ regimenes: [], usos_cfdi: [] });
  const [constancias, setConstancias] = useState([]);
  const [errores, setErrores] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [confirmo, setConfirmo] = useState(false);

  const cargar = useCallback(async () => {
    const auth = { Authorization: `Bearer ${getToken() || ""}` };
    const [c, p, mp, ms] = await Promise.all([
      fetch(`${API}/api/fiscal/catalogos`, { headers: auth }),
      fetch(`${API}/api/fiscal/perfil`, { headers: auth }),
      fetch(`${API}/api/fiscal/mis-pagos`, { headers: auth }),
      fetch(`${API}/api/fiscal/mis-solicitudes`, { headers: auth }),
    ]);
    if (c.ok) setCatalogos(await c.json());
    if (p.ok) {
      const d = await p.json();
      if (d.perfil) setForm({ ...VACIO, ...d.perfil });
      setConstancias(d.constancias || []);
    }
    if (mp.ok) setPagos(await mp.json());
    if (ms.ok) setSolicitudes(await ms.json());
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const regimenesAplicables = catalogos.regimenes.filter(
    (r) => (form.person_type === "moral" ? r.moral : r.fisica));
  const usosAplicables = catalogos.usos_cfdi.filter(
    (u) => !form.tax_regime || u.regimenes.includes(form.tax_regime));

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setErrores([]);
    const r = await fetch(`${API}/api/fiscal/perfil`, {
      method: "PUT", headers: cabeceras(), body: JSON.stringify(form),
    });
    setGuardando(false);
    if (r.ok) { toast.success("Perfil fiscal guardado"); cargar(); return; }
    const d = await r.json().catch(() => ({}));
    const lista = d?.detail?.errores || [d?.detail || "No se pudo guardar"];
    setErrores(lista);
    toast.error("Revisa los datos fiscales");
  };

  const subirConstancia = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    if (!confirmo) { toast.error("Confirma primero que tus datos coinciden con la constancia"); return; }
    const fd = new FormData();
    fd.append("file", archivo);
    fd.append("confirmo_datos", "true");
    const r = await fetch(`${API}/api/fiscal/constancia`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken() || ""}` },
      body: fd,
    });
    if (r.ok) { toast.success("Constancia cargada"); cargar(); return; }
    const d = await r.json().catch(() => ({}));
    toast.error((d?.detail?.errores || [d?.detail])[0] || "No se pudo cargar");
    e.target.value = "";
  };

  const pedirFactura = async (folio) => {
    try {
      const d = await fiscalAPI.requerirFactura(folio);
      toast.success(d.perfil_completo
        ? "Solicitud enviada al contador"
        : "Solicitud registrada: completa tus datos fiscales para que pueda emitirse");
      cargar();
    } catch (err) {
      toast.error(err.message || "No se pudo solicitar la factura");
    }
  };

  const descargar = async (accion) => {
    try { await accion(); } catch (err) { toast.error(err.message); }
  };

  const campo = "w-full rounded-lg border border-white/10 bg-[var(--upim-superficie-2)] px-3 py-2 text-sm text-[var(--upim-texto)] outline-none focus:border-[var(--upim-acento)]";

  return (
    <SeccionUpim testId="perfil-fiscal-page" className="min-h-screen py-16">
      <EncabezadoUpim
        etiqueta="Facturación"
        titulo="Mis datos fiscales"
        descripcion="Se usan para emitir tu CFDI 4.0. Sólo tú, el contador y la Administración Nacional pueden verlos."
        testId="perfil-fiscal-encabezado"
      />

      <div className="mt-12 grid gap-8 lg:grid-cols-[1.3fr_1fr]">
        <TarjetaUpim testId="perfil-fiscal-form-card">
          <form onSubmit={guardar} className="space-y-5" data-testid="perfil-fiscal-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="text-[var(--upim-texto-sec)]">Tipo de persona</span>
                <select className={campo} data-testid="fiscal-person-type" value={form.person_type}
                  onChange={(e) => setForm({ ...form, person_type: e.target.value, tax_regime: "", cfdi_use: "" })}>
                  <option value="fisica">Persona física</option>
                  <option value="moral">Persona moral</option>
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-[var(--upim-texto-sec)]">RFC</span>
                <input className={campo} data-testid="fiscal-rfc" value={form.rfc}
                  onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                  maxLength={13} placeholder={form.person_type === "moral" ? "ABC850101XX1" : "ABCD850101XX1"} />
              </label>
            </div>

            <label className="block text-sm">
              <span className="text-[var(--upim-texto-sec)]">Nombre o razón social (exacto como en tu constancia)</span>
              <input className={campo} data-testid="fiscal-legal-name" value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
            </label>

            <div className="grid gap-4 sm:grid-cols-3">
              <label className="block text-sm">
                <span className="text-[var(--upim-texto-sec)]">CP fiscal</span>
                <input className={campo} data-testid="fiscal-zip" value={form.zip_code} maxLength={5}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value.replace(/\D/g, "") })} />
              </label>
              <label className="block text-sm">
                <span className="text-[var(--upim-texto-sec)]">Régimen fiscal</span>
                <select className={campo} data-testid="fiscal-regime" value={form.tax_regime}
                  onChange={(e) => setForm({ ...form, tax_regime: e.target.value, cfdi_use: "" })}>
                  <option value="">Selecciona…</option>
                  {regimenesAplicables.map((r) => (
                    <option key={r.clave} value={r.clave}>{r.clave} · {r.descripcion}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-[var(--upim-texto-sec)]">Uso de CFDI</span>
                <select className={campo} data-testid="fiscal-cfdi-use" value={form.cfdi_use}
                  onChange={(e) => setForm({ ...form, cfdi_use: e.target.value })}>
                  <option value="">Selecciona…</option>
                  {usosAplicables.map((u) => (
                    <option key={u.clave} value={u.clave}>{u.clave} · {u.descripcion}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block text-sm">
              <span className="text-[var(--upim-texto-sec)]">Correo para recibir la factura</span>
              <input className={campo} type="email" data-testid="fiscal-email" value={form.invoice_email}
                onChange={(e) => setForm({ ...form, invoice_email: e.target.value })} />
            </label>

            <label className="flex items-start gap-3 text-sm text-[var(--upim-texto-sec)]">
              <input type="checkbox" data-testid="fiscal-consent" checked={!!form.consent_privacy}
                onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })}
                className="mt-1 accent-[var(--upim-acento)]" />
              <span>Acepto el <a href="/aviso-privacidad" className="text-[var(--upim-acento)] underline">aviso de privacidad</a> y confirmo que estos datos son correctos.</span>
            </label>

            {errores.length > 0 && (
              <ul data-testid="fiscal-errores" className="space-y-1 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-300">
                {errores.map((e, i) => <li key={i}>· {e}</li>)}
              </ul>
            )}

            <BotonUpim type="submit" disabled={guardando} data-testid="fiscal-guardar">
              {guardando ? "Guardando…" : "Guardar datos fiscales"}
            </BotonUpim>
          </form>
        </TarjetaUpim>

        <div className="space-y-8">
          <TarjetaUpim testId="fiscal-constancia-card">
            <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--upim-texto)] md:text-lg">
              <ShieldCheck className="h-5 w-5 text-[var(--upim-acento)]" aria-hidden="true" />
              Constancia de Situación Fiscal
            </h2>
            <p className="mt-2 text-sm text-[var(--upim-texto-sec)]">
              UPIM la solicita como medio de validación y documentación. Se guarda de forma privada
              y versionada; nunca en una ruta pública.
            </p>
            <label className="mt-4 flex items-start gap-3 text-sm text-[var(--upim-texto-sec)]">
              <input type="checkbox" data-testid="fiscal-confirmo-datos" checked={confirmo}
                onChange={(e) => setConfirmo(e.target.checked)} className="mt-1 accent-[var(--upim-acento)]" />
              <span>Revisé mi constancia y los datos de arriba coinciden con ella.</span>
            </label>
            <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-[var(--upim-texto)] transition-colors hover:border-[var(--upim-acento)]">
              <Upload className="h-4 w-4" aria-hidden="true" />
              Subir PDF
              <input type="file" accept="application/pdf" className="hidden"
                data-testid="fiscal-subir-constancia" onChange={subirConstancia} />
            </label>
            <ul className="mt-4 space-y-2 text-sm" data-testid="fiscal-constancias-lista">
              {constancias.length === 0 && (
                <li className="text-[var(--upim-texto-sec)]">Aún no has cargado tu constancia.</li>
              )}
              {constancias.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3">
                  <span className="truncate text-[var(--upim-texto)]">v{c.version} · {c.filename}</span>
                  <button type="button" className="text-[var(--upim-acento)] underline"
                    data-testid={`fiscal-descargar-${c.id}`}
                    onClick={() => descargar(() => fiscalAPI.descargarConstancia(c.id, c.filename))}>
                    Descargar
                  </button>
                </li>
              ))}
            </ul>
          </TarjetaUpim>

          <TarjetaUpim testId="fiscal-solicitudes-card">
            <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--upim-texto)] md:text-lg">
              <FileText className="h-5 w-5 text-[var(--upim-acento)]" aria-hidden="true" />
              Mis facturas
            </h2>
            <ul className="mt-4 space-y-3 text-sm" data-testid="fiscal-mis-solicitudes">
              {solicitudes.length === 0 && (
                <li className="text-[var(--upim-texto-sec)]">No tienes solicitudes de factura.</li>
              )}
              {solicitudes.map((s) => (
                <li key={s.id} className="rounded-lg border border-white/10 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[var(--upim-texto)]">{s.folio || s.id.slice(0, 8)}</span>
                    <span className="text-xs uppercase tracking-wide text-[var(--upim-acento)]">{s.estado}</span>
                  </div>
                  <p className="mt-1 text-[var(--upim-texto-sec)]">
                    {s.total} {s.moneda}{s.uuid_cfdi ? ` · UUID ${s.uuid_cfdi}` : ""}
                  </p>
                  <div className="mt-2 flex gap-3">
                    {(s.archivos || []).map((a) => (
                      <button key={a.id} type="button" className="text-[var(--upim-acento)] underline"
                        data-testid={`fiscal-cfdi-${a.tipo}-${s.id}`}
                        onClick={() => descargar(
                          () => fiscalAPI.descargarCfdi(s.id, a.id, a.filename))}>
                        {a.tipo.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </TarjetaUpim>
        </div>
      </div>

      <TarjetaUpim testId="fiscal-pagos-card" className="mt-8">
        <h2 className="text-base font-semibold text-[var(--upim-texto)] md:text-lg">Mis pagos</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="fiscal-pagos-tabla">
            <thead className="text-[var(--upim-texto-sec)]">
              <tr>
                <th className="py-2 pr-4">Folio</th><th className="py-2 pr-4">Concepto</th>
                <th className="py-2 pr-4">Total</th><th className="py-2 pr-4">Estado</th>
                <th className="py-2">Factura</th>
              </tr>
            </thead>
            <tbody className="text-[var(--upim-texto)]">
              {pagos.length === 0 && (
                <tr><td colSpan={5} className="py-3 text-[var(--upim-texto-sec)]">Sin pagos registrados.</td></tr>
              )}
              {pagos.map((p, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-2 pr-4">{p.folio || "—"}</td>
                  <td className="py-2 pr-4">{p.concepto}</td>
                  <td className="py-2 pr-4">{p.total} {p.moneda}</td>
                  <td className="py-2 pr-4">{p.estado}</td>
                  <td className="py-2">
                    {p.factura_estado || (p.factura_solicitada ? "solicitada" : null) || (
                      p.estado === "completed" && p.folio ? (
                        <button type="button" className="text-[var(--upim-acento)] underline"
                          data-testid={`fiscal-requiero-factura-${p.folio}`}
                          onClick={() => pedirFactura(p.folio)}>
                          Requiero factura
                        </button>
                      ) : "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TarjetaUpim>
    </SeccionUpim>
  );
}
